import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { LeadForm } from "./LeadForm";
import { HERO_IMAGE } from "../data";
import { scrollToId } from "../hooks/useSmoothScroll";

const PROOF = ["No realtor commissions", "Fast cash offers", "Close on your timeline"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-forest pt-24 pb-16">
      <motion.div className="absolute inset-0 z-0" style={{ y, scale }}>
        <img src={HERO_IMAGE} alt="Open vacant land at golden hour" fetchPriority="high" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(13,28,19,0.92) 0%, rgba(13,28,19,0.65) 45%, rgba(13,28,19,0.35) 100%)" }} />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 w-full grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p
            className="text-terracotta text-xs md:text-sm uppercase tracking-[0.3em] font-semibold mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            data-testid="hero-preheadline"
          >
            Based in Florida · Buying Vacant Land Nationwide
          </motion.p>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-sand leading-[0.95] tracking-tight" data-testid="hero-headline">
            <MaskedLines lines={["Sell your vacant", "land for cash —"]} delay={0.15} />
            <MaskedLines lines={["nationwide."]} delay={0.45} className="text-terracotta italic" />
          </h1>

          <motion.p
            className="text-sand/75 text-base md:text-lg mt-6 max-w-lg leading-relaxed"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            data-testid="hero-subheadline"
          >
            Urbina Land Investments buys vacant land directly from owners across the U.S. — a fast, no-hassle cash offer with no realtor commissions and no listing fees. Buyers, builders, and investors are welcome too.
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4 mt-9"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-2 bg-terracotta text-white px-8 py-4 tracking-wide hover:bg-terracotta-dark transition-colors duration-300"
            >
              Get a Cash Offer <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToId("lots")}
              className="border border-sand/30 text-sand px-8 py-4 tracking-wide hover:bg-sand/10 transition-colors duration-300"
            >
              View Available Lots
            </button>
          </motion.div>

          <motion.ul
            className="flex flex-wrap gap-x-6 gap-y-2 mt-10"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }}
            data-testid="hero-social-proof"
          >
            {PROOF.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sand/70 text-sm">
                <Check size={15} className="text-terracotta" /> {p}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <LeadForm variant="glass" title="Request Your Cash Offer" subtitle="Tell us about your land — we'll do the rest." />
        </motion.div>
      </div>
    </section>
  );
};
